import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Layout, Button, Drawer, Grid, Dropdown, Space, Divider } from "antd";
import { MenuOutlined, DownOutlined } from "@ant-design/icons";

const { Header: AntHeader } = Layout;
const { useBreakpoint } = Grid;

const COLORS = {
  accent: "#c9b37e",
  accentDark: "#b3a068",
};

function Header() {
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Using simple anchor tags for hash scrolling on the home page works best
  // when combined with react-router for page navigation.

  const HEADER_HEIGHT = 88;
  const HEADER_TOP_OFFSET = 20;

  // Items for the Dropdown
  const companyItems = [
    {
      key: '1',
      label: <Link to="/our-company#about-us">About Us</Link>,
    },
    {
      key: '2',
      label: <Link to="/our-company#team">Our Team</Link>,
    },
    {
      key: '3',
      label: <Link to="/our-company#certificates">Certificates</Link>,
    },
    {
      key: '4',
      label: <Link to="/our-company#contact-us-section">Contact Info</Link>,
    },
  ];

  return (
    <>
      <AntHeader
        style={{
          position: "fixed",
          top: HEADER_TOP_OFFSET,
          left: 0,
          right: 0,
          zIndex: 1000,
          height: HEADER_HEIGHT,
          maxWidth: "94%",
          margin: "0 auto",
          background: "rgba(255,255,255,.92)",
          backdropFilter: "blur(14px)",
          borderRadius: 999,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 40px",
          boxShadow: "0 12px 30px rgba(0,0,0,.15)",
        }}
      >
        {/* LEFT NAV */}
        <div style={{ flex: 1 }}>
          {!isMobile && (
            <div style={{ display: "flex", gap: "30px", fontSize: "15px" }}>
              <Link to="/" className="nav-link">Home</Link>
              
              {/* Dropdown for Our Company */}
              <Dropdown menu={{ items: companyItems }} placement="bottom">
                 <a 
                   href="#company" // Dummy href for valid anchor
                   onClick={(e) => e.preventDefault()} 
                   className="nav-link" 
                   style={{ cursor: "pointer" }}
                 >
                   <Space>
                     Our Company
                     <DownOutlined style={{ fontSize: 10 }}/>
                   </Space>
                 </a>
              </Dropdown>

              {/* Use raw <a> tags for hash links to ensure scrolling works reliably across pages */}
              <a href="/#products" className="nav-link">Products</a>
              <a href="/#gallery" className="nav-link">Gallery</a>
              <a href="/#reviews" className="nav-link">Reviews</a>
            </div>
          )}
        </div>

        {/* LOGO CENTER */}
        <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <Link to="/" style={{ display: "flex", alignItems: "center" }}>
            <img 
              src="/logo_jpg.png" 
              alt="logo" 
              // Height 55px ensures it fits within the 88px header
              style={{ height: 78, width: "auto", objectFit: "contain", cursor: "pointer" }} 
            />
          </Link>
        </div>

        {/* RIGHT ACTION */}
        <div style={{ flex: 1, textAlign: "right" }}>
          {!isMobile && (
            // Linked to #connect div in Home.jsx
            <a href="/our-company#contact-us-section"> 
              <Button
                style={{
                  background: "#000",
                  color: "#fff",
                  borderRadius: 999,
                  height: 44,
                  padding: "0 26px",
                  border: "none"
                }}
              >
                Connect with us
              </Button>
            </a>
          )}

          {isMobile && (
            <Button
              icon={<MenuOutlined />}
              type="text"
              onClick={() => setDrawerOpen(true)}
            />
          )}
        </div>
      </AntHeader>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Link to="/" onClick={() => setDrawerOpen(false)} style={mobileLinkStyle}>Home</Link>
        <Divider style={{ margin: "10px 0" }} />
        
        <div style={{ paddingLeft: 0, fontWeight: "bold", marginBottom: 10, color: COLORS.accentDark }}>Our Company</div>
        <Link to="/our-company#about-us" onClick={() => setDrawerOpen(false)} style={{...mobileLinkStyle, paddingLeft: 20, fontSize: 16}}>About Us</Link>
        <Link to="/our-company#team" onClick={() => setDrawerOpen(false)} style={{...mobileLinkStyle, paddingLeft: 20, fontSize: 16}}>Our Team</Link>
        <Link to="/our-company#certificates" onClick={() => setDrawerOpen(false)} style={{...mobileLinkStyle, paddingLeft: 20, fontSize: 16}}>Certificates</Link>
        
        <Divider style={{ margin: "10px 0" }} />
        <a href="/#products" onClick={() => setDrawerOpen(false)} style={mobileLinkStyle}>Products</a>
        <a href="/#gallery" onClick={() => setDrawerOpen(false)} style={mobileLinkStyle}>Gallery</a>
        <a href="/#reviews" onClick={() => setDrawerOpen(false)} style={mobileLinkStyle}>Reviews</a>
        <a href="/#connect" onClick={() => setDrawerOpen(false)} style={mobileLinkStyle}>Contact Us</a>
      </Drawer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&family=Montserrat:wght@400;600;700;800&display=swap');
        body, .ant-typography, div, span, p, input, textarea { font-family: 'Lato', sans-serif; }
        h1, h2, h3, h4, h5, .ant-typography h*, .ant-card-head-title, .ant-drawer-title, .ant-btn {
          font-family: 'Montserrat', sans-serif !important;
        }
        .nav-link {
          position: relative;
          font-weight: 600;
          color: #000;
          text-decoration: none;
        }
        .nav-link::after {
          content: "";
          position: absolute;
          bottom: -6px;
          left: 0;
          width: 0;
          height: 2px;
          background: ${COLORS.accent};
          transition: width .3s;
        }
        .nav-link:hover::after { width: 100%; }
      `}</style>
    </>
  );
}

const mobileLinkStyle = {
  fontSize: 18, 
  display: "block", 
  padding: "12px 0",
  color: "#000",
  textDecoration: "none"
};

export default Header;